import React from 'react';

import { ReactSerializer } from '@atlaskit/renderer';
import type { ReactSerializerInit } from '@atlaskit/renderer';

import { expVal } from '@confluence/feature-experiments';

import type { ChildInfoOutput } from './NodeTypeCounts';
import { getNodeTypeCounts } from './NodeTypeCounts';

export class VirtualizedReactSerializer extends ReactSerializer {
	// This stack is used to keep track of the counts of node types of child nodes as they are recursively
	// serialized depth first. The last index is the top of the stack.
	private vrs_childInfoOutputStack: ChildInfoOutput[];
	private vrs_isVirtualizedTablesEnabled: boolean;

	constructor(init: ReactSerializerInit) {
		super(init);
		this.vrs_childInfoOutputStack = [];
		this.vrs_isVirtualizedTablesEnabled = expVal(
			'confluence_frontend_virtualized_tables',
			'isEnabled',
			false,
		);
	}

	/**
	 * Like {@link ReactSerializer.resetState}. Named differently because a class cannot have a separate declaration of a private property of its base class.
	 */
	private vrs_resetVirtualizedState() {
		this.vrs_childInfoOutputStack = [];
	}

	/**
	 * @override
	 */
	serializeFragment(
		_fragment: SerializeFragmentParameters[0],
		props?: SerializeFragmentParameters[1],
		_target?: SerializeFragmentParameters[2],
		key?: SerializeFragmentParameters[3],
		parentInfo?: SerializeFragmentParameters[4],
	): JSX.Element | null {
		if (!this.vrs_isVirtualizedTablesEnabled) {
			return super.serializeFragment.apply(
				this,
				arguments as unknown as SerializeFragmentParameters,
			);
		}

		const isRoot = key === undefined; // Like ReactSerializer.serializeFragment
		if (isRoot) {
			this.vrs_resetVirtualizedState();
		}

		this.vrs_preSerializeFragment();
		const nodeElement = super.serializeFragment.apply(
			this,
			arguments as unknown as SerializeFragmentParameters,
		);
		const thisNodeTypeCounts = this.vrs_postSerializeFragment(parentInfo);

		if (nodeElement && props?.nodeType === 'table') {
			return React.cloneElement(nodeElement, {
				childInfo: { nodeTypeCounts: thisNodeTypeCounts },
			});
		}

		return nodeElement;
	}

	private vrs_preSerializeFragment = () => {
		// Adds a new object onto childInfoOutputStack for this node. This allows children to report their
		// estimated heights when they are serialized.
		this.vrs_childInfoOutputStack.push({ childNodeTypeCounts: [] });
	};

	private vrs_postSerializeFragment = (parentInfo: SerializeFragmentParameters[4]) => {
		// Removes the new object from childInfoOutputStack for this node.
		const thisNodesChildInfoOutput = this.vrs_childInfoOutputStack.pop() || {
			childNodeTypeCounts: [],
		};

		// Get the node type counts for this node based on its type and the counts of its children.
		const thisNode = parentInfo?.path?.[parentInfo.path.length - 1];
		const thisNodeTypeCounts = thisNode
			? getNodeTypeCounts(thisNode, thisNodesChildInfoOutput)
			: {};

		// Reports the counts for the node types of this node and its children to its parent node.
		if (this.vrs_childInfoOutputStack.length > 0) {
			// Pushes the aggregated nodeTypeCounts for the parent node to use.
			this.vrs_childInfoOutputStack[
				this.vrs_childInfoOutputStack.length - 1
			].childNodeTypeCounts.push(thisNodeTypeCounts);
		}

		return thisNodeTypeCounts;
	};
}

type SerializeFragmentParameters = Parameters<ReactSerializer['serializeFragment']>;
